import api from "./client";

// Types based on backend models
export interface ResourceListItem {
  resource_type: string;
  resource_id: string;
  stream_type: string;
  display_name: string;
  position: number;
}

export interface ResourceList {
  id?: string;
  name: string;
  description: string;
  items: ResourceListItem[];
  created_by?: string;
  client_id?: string;
  created_at?: string;
  updated_at?: string;
  color?: string;
  icon?: string;
  position?: number;
  is_favourite?: boolean;
}

export interface ResourceListItemRequest {
  resource_type: string;
  resource_id: string;
  stream_type: string;
  display_name: string;
}

export interface BulkResourceListItemRequest {
  items: ResourceListItemRequest[];
}

export interface BulkDeleteResourceListItemRequest {
  resource_ids: string[];
}

// API functions
export const fetchResourceLists = async (): Promise<ResourceList[]> => {
  const response = await api.get("/playground/resource-lists");
  return response.data;
};

export const fetchResourceListById = async (
  resourceListId: string
): Promise<ResourceList> => {
  const response = await api.get(`/playground/resource-lists/${resourceListId}`);
  return response.data;
};

export const saveResourceList = async (
  resourceList: ResourceList
): Promise<ResourceList> => {
  const response = await api.post("/playground/resource-lists", resourceList);
  return response.data;
};

export const deleteResourceList = async (
  resourceListId: string
): Promise<{ success: boolean }> => {
  const response = await api.delete(`/playground/resource-lists/${resourceListId}`);
  return response.data;
};

export const addItemsToResourceList = async (
  resourceListId: string,
  items: ResourceListItemRequest | BulkResourceListItemRequest
): Promise<string> => {
  const response = await api.post(
    `/playground/resource-lists/${resourceListId}/items`,
    items
  );
  return response.data;
};

export const removeItemFromResourceList = async (
  resourceListId: string,
  resourceId: string
): Promise<{ success: boolean }> => {
  const response = await api.delete(
    `/playground/resource-lists/${resourceListId}/items/${resourceId}`
  );
  return response.data;
};

export const removeItemsFromResourceList = async (
  resourceListId: string,
  resourceIds: string[]
): Promise<{ success: boolean }> => {
  const response = await api.delete(
    `/playground/resource-lists/${resourceListId}/items/bulk`,
    { data: { resource_ids: resourceIds } }
  );
  return response.data;
};

/**
 * Update the order of resource lists
 */
export const updateResourceListOrder = async (order: string[]): Promise<boolean> => {
  try {
    const response = await api.put("/playground/resource-lists/order", order);

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error updating resource list order:", error);
    return false;
  }
};

/**
 * Update the display order of favourite resource lists
 * Order is stored centrally in company preferences
 * @param order - Array of resource list IDs (raw UUIDs, no prefix needed)
 */
export const updateFavouriteOrder = async (order: string[]): Promise<boolean> => {
  try {
    const response = await api.put(
      "/playground/resource-lists/favourite-order",
      order
    );

    if (!response || response.status !== 200) {
      console.error(`API error: ${response?.status} ${response?.statusText}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error updating favourite order:", error);
    return false;
  }
};